import { Button, Field, makeStyles, Select, Spinner, tokens } from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import * as React from "react";
import { messageHistoryAtom } from "@/state";
import type { ChatSelection } from "@/utils/chat-selection";
import type { useOfficeConfiguration } from "@/hooks/useOfficeConfiguration";
import { CUSTOM_FUNCTIONS_NAMESPACE } from "@/utils/formula-namespace";

export default function RequestSelection({
  configuration,
  children,
}: {
  configuration: ReturnType<typeof useOfficeConfiguration>;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  const {
    selection,
    update,
    organizations,
    organization,
    organizationId,
    product,
    funds,
    fund,
    aumPeriods,
    requests,
    periods,
    officeSelection,
    isPerformance,
    isFirm,
    isFund,
    isDueDiligence,
  } = configuration;
  const messageHistory = useAtomValue(messageHistoryAtom);
  const [expanded, setExpanded] = React.useState(true);
  React.useEffect(() => {
    if (messageHistory.length > 0) setExpanded(false);
  }, [messageHistory.length]);
  const periodQuery = isFirm ? aumPeriods : funds;
  const ready = !!officeSelection;
  const formulaName = `${CUSTOM_FUNCTIONS_NAMESPACE}.${isDueDiligence ? "ASK" : "PERFORMANCE"}`;

  const change = (value: Partial<ChatSelection>) => update(value);
  const summaryTitle = isDueDiligence ? "Due diligence" : isFirm ? "Firm AUM" : "Fund performance";
  const summaryDetail = isDueDiligence
    ? [product?.name, requests.data?.find((item) => item.id === selection.requestId)?.title]
    : [isFund ? fund?.name : null, periods?.find((item) => item.importId === selection.reportId)?.label];
  const requestGroups = [...new Set(requests.data?.map((item) => item.status.name) ?? [])];
  const standardStatuses = ["To Do", "In Progress", "Done"];
  requestGroups.sort((a, b) => {
    const rank = (status: string) => (standardStatuses.includes(status) ? standardStatuses.indexOf(status) : 3);
    return rank(a) - rank(b);
  });

  return (
    <>
      {ready && !expanded ? (
        <section className={styles.summary} aria-label="Selected worksheet and chat context">
          <div className={styles.summaryText}>
            <strong>{summaryTitle}</strong>
            <p className={styles.hint}>{[organization?.name, ...summaryDetail].filter(Boolean).join(" · ")}</p>
          </div>
          <Button className={styles.changeButton} size="small" onClick={() => setExpanded(true)}>
            Change
          </Button>
        </section>
      ) : (
        <section className={styles.container} aria-label="Worksheet and chat request selection">
          <div>
            <h2 className={styles.heading}>What would you like to ask about?</h2>
            <p className={styles.hint}>Choose the source for worksheet formulas and chat.</p>
          </div>
          <QueryStatus query={organizations} loading="Loading organizations…" />
          {organizations.data?.length === 0 && (
            <p className={styles.hint}>You don’t have access to an organization yet.</p>
          )}
          {(organizations.data?.length ?? 0) > 1 && (
            <Field className={styles.field} label="Organization">
              <Select
                className={styles.select}
                value={organizationId}
                onChange={(_, data) => change({ organizationId: data.value })}
              >
                <option value="">Select an organization</option>
                {organizations.data?.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {!!organization && (
            <Field className={styles.field} label="Request type">
              <Select
                className={styles.select}
                value={selection.requestType}
                onChange={(_, data) => change({ requestType: data.value as ChatSelection["requestType"] })}
              >
                <option value="">Select a request type</option>
                <option value="performance">Performance request</option>
                <option value="due-diligence">Due diligence request</option>
              </Select>
            </Field>
          )}
          {!!organization && isPerformance && (
            <Field className={styles.field} label="Performance for">
              <Select
                className={styles.select}
                value={selection.scope}
                onChange={(_, data) => change({ scope: data.value as ChatSelection["scope"] })}
              >
                <option value="">Select firm or product</option>
                <option value="firm">Firm — AUM</option>
                <option value="product">Product — fund performance</option>
              </Select>
            </Field>
          )}
          {!!organization && isFund && (
            <>
              <QueryStatus query={funds} loading="Loading funds…" />
              <Field className={styles.field} label="Fund">
                <Select
                  className={styles.select}
                  value={fund?.id ?? ""}
                  disabled={!funds.data?.length || funds.isError}
                  onChange={(_, data) => {
                    const selected = funds.data?.find((item) => item.id === data.value);
                    change({ fundId: selected?.id ?? "", productId: selected?.productId ?? "" });
                  }}
                >
                  <option value="">Select a fund</option>
                  {funds.data?.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                      {organization.products.length > 1
                        ? ` (${organization.products.find((p) => p.id === item.productId)?.name ?? ""})`
                        : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              {funds.data?.length === 0 && (
                <p className={styles.hint}>No approved fund reports are available in this organization.</p>
              )}
            </>
          )}
          {!!organization && (isFirm || (isFund && !!fund)) && (
            <>
              {isFirm && <QueryStatus query={aumPeriods} loading="Loading reporting periods…" />}
              <Field className={styles.field} label="Reporting period">
                <Select
                  className={styles.select}
                  value={periods?.some((item) => item.importId === selection.reportId) ? selection.reportId : ""}
                  disabled={!periods?.length || periodQuery.isError}
                  onChange={(_, data) => change({ reportId: data.value })}
                >
                  <option value="">Select a reporting period</option>
                  {periods?.map((item) => (
                    <option key={item.importId} value={item.importId}>
                      {item.label}
                    </option>
                  ))}
                </Select>
              </Field>
              {isFirm && aumPeriods.data?.length === 0 && (
                <p className={styles.hint}>
                  No completed AUM reports are available. Add a tracker in the web app first.
                </p>
              )}
            </>
          )}
          {!!organization && isDueDiligence && (
            <>
              <Field className={styles.field} label="Fund">
                <Select
                  className={styles.select}
                  value={product?.id ?? ""}
                  disabled={!organization.products.length}
                  onChange={(_, data) => change({ productId: data.value })}
                >
                  <option value="">Select a fund</option>
                  {organization.products.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {!organization.products.length && (
                <p className={styles.hint}>No funds are available in this organization.</p>
              )}
              {!!product && (
                <>
                  <QueryStatus query={requests} loading="Loading requests…" />
                  <Field className={styles.field} label="Kanban request">
                    <Select
                      className={styles.select}
                      value={requests.data?.some((item) => item.id === selection.requestId) ? selection.requestId : ""}
                      disabled={!requests.data?.length || requests.isError}
                      onChange={(_, data) => change({ requestId: data.value })}
                    >
                      <option value="">Select a request</option>
                      {requestGroups.map((status) => (
                        <optgroup key={status} label={status}>
                          {requests.data
                            ?.filter((item) => item.status.name === status)
                            .map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.title}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                    </Select>
                  </Field>
                  {requests.data?.length === 0 && (
                    <p className={styles.hint}>
                      No Kanban requests are available for this fund. Create one in the web app first.
                    </p>
                  )}
                </>
              )}
            </>
          )}
          {messageHistory.length > 0 && <p className={styles.hint}>Changing a selection starts a new chat.</p>}
          {ready && (
            <Button appearance="primary" onClick={() => setExpanded(false)}>
              Use this selection
            </Button>
          )}
        </section>
      )}
      {ready && (
        <div className={styles.formulaHint}>
          <p className={styles.hint}>
            This selection powers <strong>{formulaName}</strong> and chat.
          </p>
          <p className={styles.hint}>
            Enter <code>{`=${formulaName}(A2)`}</code> in B2, then fill down for each question.
          </p>
          <p className={styles.hint}>Recalculate existing formulas after changing this selection.</p>
        </div>
      )}
      {children}
    </>
  );
}

function QueryStatus({
  query,
  loading,
}: {
  query: { isPending: boolean; isError: boolean; isFetching: boolean; refetch: () => unknown };
  loading: string;
}) {
  if (query.isError)
    return (
      <div role="alert">
        Could not load these choices.{" "}
        <Button size="small" onClick={() => query.refetch()}>
          Retry
        </Button>
      </div>
    );
  if (query.isPending && query.isFetching) return <Spinner size="tiny" label={loading} />;
  return null;
}

const useStyles = makeStyles({
  summary: {
    display: "flex",
    minWidth: 0,
    alignItems: "flex-start",
    gap: "12px",
    justifyContent: "space-between",
    paddingBottom: "12px",
  },
  summaryText: { flex: 1, minWidth: 0 },
  changeButton: { flexShrink: 0 },
  container: { display: "flex", flexDirection: "column", minWidth: 0, gap: "12px", paddingBottom: "16px" },
  field: { minWidth: 0, gridTemplateColumns: "minmax(0, 1fr)" },
  select: {
    minWidth: 0,
    width: "100%",
    "& select": { minWidth: 0, width: "100%", textOverflow: "ellipsis" },
  },
  formulaHint: { paddingBottom: "12px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  heading: { fontSize: "16px", lineHeight: "22px", margin: 0, fontWeight: 600 },
  hint: {
    margin: "4px 0 0",
    fontSize: "12px",
    lineHeight: "18px",
    color: tokens.colorNeutralForeground2,
    overflowWrap: "anywhere",
  },
});
